import { TranslationScopes } from "@finbackoffice/enums";
import {
    AuthContext,
    CasinoFavoritesContext,
    CasinoScopesMap,
    useLayoutCasinoConfig,
    useRuntimeConfig,
} from "@finbackoffice/site-core";
import clsx from "clsx";
import { useRouter } from "next/router";
import { FC, useContext, useCallback, useMemo } from "react";
import {
    CasinoFilterPropType,
    CasinoFilterTypeEnum,
    CasinoGameTypeEnum,
    formatAppNameText,
    ILayoutsCasinoSectionsFilterConfig,
} from "@finbackoffice/fe-core";
import ActiveLink from "components/base/active-link/ActiveLink";
import Button from "components/base/button/Button";
import { Svg } from "components/base/svg/Svg";
import Translate from "components/base/translate/Translate";
import CasinoProvidersList from "components/pages/casino/providers/CasinoProvidersList";
import CasinoSearchBlock from "components/pages/casino/search/CasinoSearchBlock";
import { CasinoToolsContext } from "contexts";
import { Chars } from "utils/strings";
import { RouteNames } from "utils/constants";
import styles from "../../../styles/casino-compact-layout.module.sass";

const CasinoTagFilters: FC = () => {
    const { casinoFiltersConfig } = useLayoutCasinoConfig("slots");
    const casinoTagFiltersConfig = useMemo(
        () => casinoFiltersConfig.filter((config) => config.type === CasinoFilterTypeEnum.Tag),
        [casinoFiltersConfig],
    );

    const renderTagFilter = useCallback((filter: ILayoutsCasinoSectionsFilterConfig) => {
        if (filter.enable) {
            const tag = filter.value;
            return (
                <ActiveLink
                    key={tag}
                    href={{
                        pathname: `/${RouteNames.CASINO}/${RouteNames.SLOTS}/tag/${tag}`,
                        query: undefined,
                    }}
                    includes={`/${RouteNames.CASINO}/${RouteNames.SLOTS}/tag/${tag}`}
                    activeClassName={styles.active}
                    shallow>
                    <>
                        <Svg
                            src={`/common/casino/filters/${tag.toLowerCase()}.svg`}
                            wrapper="span"
                            className="svg-icon"
                        />
                        <span>
                            <Translate
                                tid={`casino_${tag.toLowerCase()}`}
                                namespace={TranslationScopes.Casino}
                            />
                        </span>
                    </>
                </ActiveLink>
            );
        }
        return null;
    }, []);

    return casinoTagFiltersConfig.map(renderTagFilter);
};

type IProps = {
    onGameTypeChange: (val: CasinoGameTypeEnum[]) => void;
    updateFilterQuery: (query?: CasinoFilterPropType) => void;
};

const CasinoFilters: FC<IProps> = ({ onGameTypeChange, updateFilterQuery }) => {
    const router = useRouter();
    const { casinoFiltersConfig } = useLayoutCasinoConfig("slots");
    const casinoFiltersConfigMemo = useMemo(
        () => casinoFiltersConfig.filter((config) => config.type !== CasinoFilterTypeEnum.Tag),
        [casinoFiltersConfig],
    );

    const onGamePropChange = useCallback(
        async (val: CasinoFilterPropType) => {
            updateFilterQuery(val);
        },
        [updateFilterQuery],
    );

    const renderFilter = useCallback(
        (filter: ILayoutsCasinoSectionsFilterConfig) => {
            let content = null;
            if (filter.enable) {
                switch (filter.type) {
                    case CasinoFilterTypeEnum.Prop:
                        const prop = filter.value as CasinoFilterPropType;
                        content = (
                            <Button
                                key={prop}
                                type="button"
                                onClick={() =>
                                    router.query.filter !== prop && onGamePropChange(prop)
                                }
                                className={clsx(router.query.filter === prop && styles.active)}>
                                <Svg
                                    src={`/common/casino/filters/${prop}.svg`}
                                    wrapper="span"
                                    className="svg-icon"
                                />
                                <span>
                                    <Translate
                                        tid={`casino_${prop}`}
                                        namespace={TranslationScopes.Casino}
                                    />
                                </span>
                            </Button>
                        );
                        break;
                    case CasinoFilterTypeEnum.Type:
                        const gameType = filter.value;
                        content = (
                            <Button
                                key={gameType}
                                type="button"
                                onClick={() =>
                                    router.query.filter !== gameType && onGameTypeChange([gameType])
                                }
                                className={clsx(router.query.filter === gameType && styles.active)}>
                                <Svg
                                    src={`/common/casino/filters/${gameType}.svg`}
                                    wrapper="span"
                                    className="svg-icon"
                                />
                                <span>
                                    <Translate
                                        tid={`casino_${gameType.replaceAll("-", "_")}`}
                                        namespace={TranslationScopes.Casino}
                                    />
                                </span>
                            </Button>
                        );
                        break;
                    default:
                        content = null;
                        break;
                }
            }

            return content;
        },
        [onGamePropChange, onGameTypeChange, router.query],
    );

    return casinoFiltersConfigMemo.map(renderFilter);
};

const SlotsTools: FC = () => {
    const router = useRouter();
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { gameTypes, providerOptions } = useContext(CasinoToolsContext);
    const { isUserLoggedIn } = useContext(AuthContext);
    const { favoriteGamesState } = useContext(CasinoFavoritesContext);

    const updateFilterQuery = useCallback(
        (val?: string) => {
            const dir = `/${RouteNames.CASINO}/${RouteNames.SLOTS}`;
            const shouldChangeRoute = router.pathname !== dir;
            if (shouldChangeRoute || !!router.query.filter || val) {
                const routerQuery = { ...router.query };
                if (routerQuery.tag) {
                    delete routerQuery.tag;
                }
                if (val) {
                    routerQuery.filter = val;
                } else if (routerQuery.filter) {
                    delete routerQuery.filter;
                }
                router.push({ pathname: dir, query: routerQuery }, undefined, {
                    shallow: true,
                });
            }
        },
        [router],
    );

    const onGameTypeChange = useCallback(
        (val: CasinoGameTypeEnum[]) => {
            const isSingleType = val.length === 1;

            updateFilterQuery(isSingleType ? val[0] : undefined);
        },
        [updateFilterQuery],
    );

    return (
        <>
            <div className={styles.subHeader}>
                <div>
                    <nav className={styles.nav}>
                        <Button
                            type="button"
                            onClick={() => onGameTypeChange(CasinoScopesMap.slots)}
                            className={clsx(
                                router.pathname === `/${RouteNames.CASINO}/${RouteNames.SLOTS}` &&
                                    gameTypes === CasinoScopesMap.slots &&
                                    !router.query.filter &&
                                    styles.active,
                            )}>
                            <Svg
                                src={`/${formatAppNameText(
                                    COMMON_SITE_CONFIGS.appName,
                                )}/common/base-icons/slots-icon.svg`}
                                wrapper="span"
                                className="svg-icon"
                            />
                            <span>
                                <Translate
                                    tid="casino_allTab"
                                    namespace={TranslationScopes.Casino}
                                />
                                {Chars.nbsp}
                                <Translate tid="pageName_slots" />
                            </span>
                        </Button>

                        <CasinoFilters
                            onGameTypeChange={onGameTypeChange}
                            updateFilterQuery={updateFilterQuery}
                        />
                    </nav>
                    <div className={styles.casinoSearch}>
                        <CasinoSearchBlock
                            gameTypes={CasinoScopesMap.slots}
                            showSearchInputDefault={false}
                        />
                    </div>
                </div>
            </div>
            <div className={styles.leftMenu}>
                {isUserLoggedIn && (
                    <ActiveLink
                        href={`/${RouteNames.CASINO}/${RouteNames.SLOTS}/${RouteNames.FAVORITES}`}
                        activeClassName={styles.active}>
                        <>
                            <Svg
                                src="/common/desktop/casino/favorites.svg"
                                wrapper="span"
                                className="svg-icon"
                            />
                            {!!favoriteGamesState.games.length && (
                                <i className={styles.favoritesCount}>
                                    {favoriteGamesState.games.length}
                                </i>
                            )}
                            <span>
                                <Translate
                                    tid="casino_favorites"
                                    namespace={TranslationScopes.Casino}
                                />
                            </span>
                        </>
                    </ActiveLink>
                )}
                <CasinoTagFilters />
            </div>
            {providerOptions.length > 1 && <CasinoProvidersList />}
        </>
    );
};

export default SlotsTools;
